import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface Summary {
  windowDays: number;
  totalInWindow: number;
  totalAllTime: number;
  byPath: Array<{ path: string; count: number }>;
  byDay: Array<{ day: string; count: number }>;
}

const RANGES = [7, 30, 90] as const;

export function Analytics(): React.JSX.Element {
  const [days, setDays] = useState<(typeof RANGES)[number]>(30);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'analytics', 'summary', days],
    queryFn: () => api<Summary>('/admin/analytics/summary', { query: { days } }),
  });

  const chartData = (data?.byDay ?? []).map((d) => ({
    day: new Date(d.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    count: d.count,
  }));

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Analytics</h1>
          <p className="text-sm text-muted-foreground">Page views from the public site.</p>
        </div>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <Button
              key={r}
              variant={days === r ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDays(r)}
            >
              {r}d
            </Button>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Views in window
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold">
              {(data?.totalInWindow ?? 0).toLocaleString()}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total all time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-semibold">
              {(data?.totalAllTime ?? 0).toLocaleString()}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Daily views</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="h-64 text-sm text-muted-foreground">Loading…</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Top paths</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Path</TableHead>
                <TableHead className="text-right">Views</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.byPath ?? []).map((row) => (
                <TableRow key={row.path}>
                  <TableCell className="font-mono text-xs">{row.path}</TableCell>
                  <TableCell className="text-right">{row.count.toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {(data?.byPath.length ?? 0) === 0 && (
                <TableRow>
                  <TableCell colSpan={2} className="text-center text-muted-foreground">
                    No data yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
